import React, { useState, useEffect } from 'react';
import { Home, TrendingUp, Clock, CheckCircle, AlertTriangle, DollarSign, Users, Star, Calendar, Search, Download, Plus, Filter, Target, Zap } from 'lucide-react';
import LeadDetail from './LeadDetail';

const VillaWorkflow = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedDestination, setSelectedDestination] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadDetail, setShowLeadDetail] = useState(false);

  // Statistiques principales
  const stats = {
    reservationsActives: 62,
    reservationsGrowth: 18,
    valeurPipeline: 12400000,
    pipelineGrowth: 34,
    tempsReponse: 2.1,
    tempsImprovement: -45,
    tauxConversion: 94,
    conversionGrowth: 8,
    dureeMoySejourJours: 18,
    dureeGrowth: 3,
    panierMoyen: 68000,
    panierGrowth: 12000,
    satisfaction: 4.8,
    recommandation: 98
  };

  // Étapes du workflow
  const workflowSteps = [
    {
      id: 'enquiry',
      name: 'Enquête',
      icon: '💬',
      count: 2,
      sla: '2h',
      color: 'blue'
    },
    {
      id: 'qualification',
      name: 'Qualification',
      icon: '🔍',
      count: 1,
      sla: '4h',
      color: 'indigo'
    },
    {
      id: 'proposition',
      name: 'Proposition',
      icon: '📋',
      count: 1,
      sla: '24h',
      color: 'yellow'
    },
    {
      id: 'negotiation',
      name: 'Négociation',
      icon: '🤝',
      count: 1,
      sla: '48h',
      color: 'orange'
    },
    {
      id: 'contrat',
      name: 'Contrat',
      icon: '✍️',
      count: 1,
      sla: '24h',
      color: 'purple'
    },
    {
      id: 'paiement',
      name: 'Paiement',
      icon: '💳',
      count: 1,
      sla: '3 jours',
      color: 'green'
    },
    {
      id: 'preparation',
      name: 'Préparation',
      icon: '🛎️',
      count: 1,
      sla: 'J-30 à J-1',
      color: 'teal'
    },
    {
      id: 'sejour',
      name: 'En Séjour',
      icon: '🏖️',
      count: 1,
      sla: '24/7 Support',
      color: 'pink'
    }
  ];

  // Réservations par étape
  const reservationsByStep = {
    enquiry: [
      {
        id: 'ENQ001',
        villa: 'Villa Azure',
        destination: 'St-Barth',
        client: 'Marie Dubois',
        dates: '15 Dec - 5 Jan',
        amount: 125000,
        priority: 'VIP',
        status: 'Festive',
        urgency: 'normal'
      },
      {
        id: 'ENQ002',
        villa: 'Villa Sunset',
        destination: 'Mykonos',
        client: 'John Smith',
        dates: '1-15 Aug 2025',
        amount: 85000,
        priority: null,
        status: null,
        urgency: 'urgent'
      }
    ],
    qualification: [
      {
        id: 'QUA001',
        villa: 'Villa Paradise',
        destination: 'St-Tropez',
        client: 'Chen Wei',
        dates: '10-24 Jul',
        amount: 180000,
        details: '10 guests'
      }
    ],
    proposition: [
      {
        id: 'PRO001',
        villa: 'Villa Romana',
        destination: 'Sardaigne',
        client: 'Al-Rashid Family',
        dates: '1-31 Aug',
        amount: 320000,
        priority: 'VVIP',
        details: '31 nuits'
      }
    ],
    negotiation: [
      {
        id: 'NEG001',
        villa: 'Villa Celeste',
        destination: 'Ibiza',
        client: 'Tech Founders',
        dates: '15-29 Sep',
        amount: 145000,
        status: 'Event'
      }
    ],
    contrat: [
      {
        id: 'CON001',
        villa: 'Villa Royal',
        destination: 'St-Barth',
        client: 'Emma Watson',
        dates: '20 Dec - 3 Jan',
        amount: 195000,
        priority: 'Celebrity',
        status: 'Signing'
      }
    ],
    paiement: [
      {
        id: 'PAY001',
        villa: 'Villa Marina',
        destination: 'Cannes',
        client: 'Corporate Event',
        dates: '15-22 May',
        amount: 250000,
        event: 'Film Festival',
        status: '50% reçu'
      }
    ],
    preparation: [
      {
        id: 'PREP001',
        villa: 'Villa Eden',
        destination: 'St-Barth',
        client: 'Russian Oligarch',
        dates: '1-14 Jul',
        amount: 420000,
        priority: 'VVIP',
        status: 'Staff briefing'
      }
    ],
    sejour: [
      {
        id: 'SEJ001',
        villa: 'Villa Infinity',
        destination: 'Mykonos',
        client: 'Saudi Prince',
        dates: 'Jour 5/21',
        amount: 580000,
        status: 'Active',
        service: 'Concierge 24/7'
      }
    ]
  };

  // Tâches urgentes
  const urgentTasks = [
    {
      id: 'TASK001',
      type: 'urgent',
      title: 'Contrat à finaliser',
      description: 'Villa Royal St-Barth - Emma Watson attend la version finale du contrat avec amendements demandés',
      deadline: '2h',
      assignee: 'Legal Team',
      icon: '🚨'
    },
    {
      id: 'TASK002',
      type: 'high',
      title: 'Validation Chef Privé',
      description: 'Villa Marina Cannes - Confirmer disponibilité Chef Michelin pour event corporate Film Festival',
      deadline: 'Aujourd\'hui',
      assignee: 'Concierge',
      icon: '👨‍🍳'
    },
    {
      id: 'TASK003',
      type: 'normal',
      title: 'Transferts Hélicoptère',
      description: 'Villa Infinity Mykonos - Organiser transferts Athens-Mykonos pour famille Saudi (12 pax)',
      deadline: 'J-7',
      assignee: 'Transport Team',
      icon: '✈️'
    }
  ];

  // Checklist pré-arrivée
  const checklistItems = [
    { id: 1, task: 'Contrat signé et archivé', completed: true },
    { id: 2, task: 'Acompte 50% reçu', completed: true },
    { id: 3, task: 'Solde payé', completed: true },
    { id: 4, task: 'Préférences alimentaires collectées', completed: false, deadline: 'J-30' },
    { id: 5, task: 'Staff briefé (majordome, chef, femmes de chambre)', completed: false, deadline: 'J-7' },
    { id: 6, task: 'Transferts aéroport confirmés', completed: false, deadline: 'J-14' },
    { id: 7, task: 'Welcome basket commandé', completed: false, deadline: 'J-3' },
    { id: 8, task: 'Inspection finale villa', completed: false, deadline: 'J-2' },
    { id: 9, task: 'Codes d\'accès générés', completed: false, deadline: 'J-1' },
    { id: 10, task: 'Instructions check-in envoyées', completed: false, deadline: 'J-1' }
  ];

  // Règles d'automatisation
  const automationRules = [
    {
      trigger: 'Nouvelle enquête reçue',
      actions: ['Email auto confirmation', 'Check disponibilité', 'Assign commercial'],
      icon: '💬'
    },
    {
      trigger: 'Contrat signé',
      actions: ['Facture acompte 50%', 'Bloquer dates', 'Notifier propriétaire'],
      icon: '✍️'
    },
    {
      trigger: 'Paiement complet reçu',
      actions: ['Formulaire préférences', 'Welcome guide', 'Brief équipe villa'],
      icon: '💳'
    },
    {
      trigger: 'J-30 avant arrivée',
      actions: ['Concierge contact', 'Services additionnels', 'Activités'],
      icon: '📅'
    },
    {
      trigger: '24h avant arrivée',
      actions: ['Instructions check-in', 'Codes', 'Contact majordome', 'Météo'],
      icon: '⏰'
    },
    {
      trigger: '3 jours après arrivée',
      actions: ['Quality check WhatsApp', 'Propositions services', 'Upsell'],
      icon: '🏖️'
    },
    {
      trigger: 'Jour de départ',
      actions: ['Check-out reminder', 'Inspection planning', 'Inventaire'],
      icon: '👋'
    },
    {
      trigger: '2 jours après départ',
      actions: ['Thank you', 'Review request', 'Offre fidélité', 'Album photos'],
      icon: '✅'
    }
  ];

  const getStepColor = (color) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200',
      indigo: 'bg-indigo-50 border-indigo-200',
      yellow: 'bg-yellow-50 border-yellow-200',
      orange: 'bg-orange-50 border-orange-200',
      purple: 'bg-purple-50 border-purple-200',
      green: 'bg-green-50 border-green-200',
      teal: 'bg-teal-50 border-teal-200',
      pink: 'bg-pink-50 border-pink-200'
    };
    return colors[color] || 'bg-gray-50 border-gray-200';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'VVIP': return 'bg-purple-100 text-purple-800';
      case 'VIP': return 'bg-yellow-100 text-yellow-800';
      case 'Celebrity': return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'normal': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setShowLeadDetail(true);
  };

  const handleLeadSave = (updatedLead) => {
    console.log('Lead updated:', updatedLead);
    // Ici on mettrait à jour les données du lead
    setShowLeadDetail(false);
    setSelectedLead(null);
  };

  const renderLeadCard = (reservation) => {
    // Function implementation would go here
  };

  return (
    <div className="villa-workflow">
      {showLeadDetail ? (
        <LeadDetail 
          lead={selectedLead} 
          onBack={() => {
            setShowLeadDetail(false);
            setSelectedLead(null);
          }}
          onSave={handleLeadSave}
        />
      ) : (
        <>
          {/* En-tête */}
          <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Workflow Location Villas de Luxe</h1>
            <p className="text-gray-600 mt-1">🏡 Premium Properties - Gestion complète du processus de location</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <Filter size={18} />
              <span>Filtres</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
              <Download size={18} />
              <span>Export</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <Plus size={18} />
              <span>Nouvelle Réservation</span>
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-3 mt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <Users className="text-blue-600" size={20} />
              <span className="text-xs font-medium text-green-600">↑ {stats.reservationsGrowth}%</span>
            </div>
            <div className="text-xl font-bold text-gray-800">{stats.reservationsActives}</div>
            <div className="text-xs text-gray-600">Réservations Actives</div>
            <div className="text-xs text-gray-500">vs mois dernier</div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <DollarSign className="text-green-600" size={20} />
              <span className="text-xs font-medium text-green-600">↑ {stats.pipelineGrowth}%</span>
            </div>
            <div className="text-xl font-bold text-gray-800">€{(stats.valeurPipeline / 1000000).toFixed(1)}M</div>
            <div className="text-xs text-gray-600">Valeur Pipeline</div>
            <div className="text-xs text-gray-500">YoY</div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <Clock className="text-yellow-600" size={20} />
              <span className="text-xs font-medium text-green-600">↓ {Math.abs(stats.tempsImprovement)}%</span>
            </div>
            <div className="text-xl font-bold text-gray-800">{stats.tempsReponse}h</div>
            <div className="text-xs text-gray-600">Temps Réponse</div>
            <div className="text-xs text-gray-500">amélioration</div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <Target className="text-purple-600" size={20} />
              <span className="text-xs font-medium text-green-600">↑ {stats.conversionGrowth}%</span>
            </div>
            <div className="text-xl font-bold text-gray-800">{stats.tauxConversion}%</div>
            <div className="text-xs text-gray-600">Taux Conversion</div>
            <div className="text-xs text-gray-500">ce trimestre</div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <Calendar className="text-orange-600" size={20} />
              <span className="text-xs font-medium text-green-600">↑ {stats.dureeGrowth}</span>
            </div>
            <div className="text-xl font-bold text-gray-800">{stats.dureeMoySejourJours}</div>
            <div className="text-xs text-gray-600">Durée Moy. Séjour</div>
            <div className="text-xs text-gray-500">jours</div>
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <DollarSign className="text-teal-600" size={20} />
              <span className="text-xs font-medium text-green-600">↑ €{(stats.panierGrowth / 1000).toFixed(0)}K</span>
            </div>
            <div className="text-xl font-bold text-gray-800">€{(stats.panierMoyen / 1000).toFixed(0)}K</div>
            <div className="text-xs text-gray-600">Panier Moyen</div>
            <div className="text-xs text-gray-500">vs 2024</div>
          </div>

          <div className="bg-pink-50 border border-pink-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <Star className="text-pink-600" size={20} />
            </div>
            <div className="text-xl font-bold text-gray-800">{stats.satisfaction}/5</div>
            <div className="text-xs text-gray-600">Satisfaction</div>
            <div className="text-xs text-gray-500">{stats.recommandation}% recommandent</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Filtres */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <button className="px-3 py-2 text-sm bg-yellow-50 border border-yellow-300 text-yellow-800 rounded-lg">Toutes</button>
            <button className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">St-Barth</button>
            <button className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">St-Tropez</button>
            <button className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Mykonos</button>
            <button className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cette semaine</button>
            <button className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">VIP</button>
          </div>
        </div>

        {/* Pipeline Kanban */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Pipeline de Réservation Villas</h3>
          <div className="flex space-x-4 overflow-x-auto pb-6">
            {workflowSteps.map((step) => (
              <div key={step.id} className={`min-w-80 ${getStepColor(step.color)} border rounded-lg p-4`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-xl">{step.icon}</span>
                    <div>
                      <h4 className="font-semibold text-gray-800">{step.name}</h4>
                      <p className="text-xs text-gray-600">SLA: {step.sla}</p>
                    </div>
                  </div>
                  <span className="text-sm font-medium text-gray-600">{step.count}</span>
                </div>
                
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {(reservationsByStep[step.id] || []).map((reservation) => (
                    <div 
                      key={reservation.id} 
                      className="bg-white border border-gray-200 rounded-lg p-3 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all duration-200"
                      onClick={() => handleLeadClick(reservation)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium text-gray-800">{reservation.villa} - {reservation.destination}</h5>
                        {reservation.priority && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(reservation.priority)}`}>
                            {reservation.priority}
                          </span>
                        )}
                        {reservation.urgency && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getUrgencyColor(reservation.urgency)}`}>
                            {reservation.urgency}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700">{reservation.client}</p>
                      <p className="text-sm text-gray-600">{reservation.dates}</p>
                      <p className="text-sm font-medium text-green-600">€{reservation.amount.toLocaleString()}</p>
                      {reservation.status && (
                        <p className="text-xs text-blue-600 mt-1">{reservation.status}</p>
                      )}
                      {reservation.details && (
                        <p className="text-xs text-gray-500">{reservation.details}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tâches urgentes */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Tâches Urgentes</h3>
              <div className="space-y-4">
                {urgentTasks.map((task) => (
                  <div key={task.id} className={`border rounded-lg p-4 ${getUrgencyColor(task.type)}`}>
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-lg">{task.icon}</span>
                      <div>
                        <h4 className="font-medium">{task.title}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getUrgencyColor(task.type)}`}>
                          {task.type.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm mb-2">{task.description}</p>
                    <div className="flex justify-between items-center text-xs">
                      <span>Échéance: {task.deadline}</span>
                      <span>Assigné: {task.assignee}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Règles d'automatisation */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Règles d'Automatisation Actives</h3>
                <button className="flex items-center space-x-2 px-3 py-1 text-blue-600 border border-blue-300 rounded hover:bg-blue-50">
                  <Plus size={16} />
                  <span>Nouvelle Règle</span>
                </button>
              </div>
              
              <div className="space-y-3">
                {automationRules.map((rule, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <span className="text-lg">{rule.icon}</span>
                    <div className="flex-1">
                      <span className="font-medium text-gray-800">{rule.trigger}</span>
                      <span className="mx-2 text-gray-400">→</span>
                      <span className="text-gray-600">{rule.actions.join(' + ')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar droite */}
          <div className="space-y-6">
            {/* Checklist */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Checklist Pré-Arrivée - Villa Royal St-Barth</h3>
              <p className="text-sm text-gray-600 mb-4">{checklistItems.filter(item => item.completed).length}/{checklistItems.length} complétés</p>
              
              <div className="space-y-3">
                {checklistItems.map((item) => (
                  <div key={item.id} className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded ${item.completed ? 'bg-green-500' : 'bg-gray-300'} flex items-center justify-center`}>
                      {item.completed && <span className="text-white text-xs">✓</span>}
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm ${item.completed ? 'text-gray-800 line-through' : 'text-gray-700'}`}>
                        {item.task}
                      </p>
                      {item.deadline && !item.completed && (
                        <p className="text-xs text-gray-500">{item.deadline}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default VillaWorkflow;